import { COMMAND_INTENTS, type CommandIntent, type CommandsCatalog, commandCatalogs } from "./commandCatalogData.js";
import { DEFAULT_LOCALE, LOCALES, type Locale } from "./constants.js";

/** Intents that take an argument, so a token may be matched as a prefix of a spaceless body. */
const ARGUMENT_TAKING_INTENTS: readonly CommandIntent[] = ["leave", "language"];

/** A token ending in one of these scripts may be followed by its argument with no separator. */
const SPACELESS_FINAL_CHARACTER = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]$/u;

const TRAILING_SENTENCE_PUNCTUATION = /[.,!?;:。、！？]+$/u;

/**
 * Drops the punctuation a grower signs a message off with. Shared with org-name matching: the parser
 * keeps the argument's own punctuation so it can be echoed back, so the matcher has to strip the same
 * alphabet the keyword lookup did, or the two disagree about what a name is.
 */
export function stripTrailingSentencePunctuation(raw: string): string {
    return raw.replace(TRAILING_SENTENCE_PUNCTUATION, "");
}

/**
 * NFKC composes rather than decomposes, so it folds full-width `ＬＩＳＴ` and half-width `ﾘｽﾄ` without
 * stripping the combining marks that Gurmukhi and kana spell words with.
 */
export function foldCommandWhitespace(raw: string): string {
    return raw.normalize("NFKC").replace(/\s+/gu, " ").trim();
}

/**
 * Applied to grower text and to every catalog token, so the two can only ever be compared in the same
 * form. `toUpperCase`, never `toLocaleUpperCase`: locale-sensitive casing would make matching depend on
 * the server's default locale.
 */
export function normalizeCommandToken(raw: string): string {
    return foldCommandWhitespace(raw).toUpperCase();
}

/**
 * Case- and accent-insensitive form for matching grower text against a name. Folds diacritics only where
 * the base letter is Latin: a blanket mark strip would empty Gurmukhi and merge Japanese kana, so a name
 * in either script would stop matching itself.
 */
export function foldForSearch(raw: string): string {
    return foldCommandWhitespace(raw)
        .normalize("NFD")
        .replace(/(\p{Script=Latin})\p{Mn}+/gu, "$1")
        .normalize("NFC")
        .toLowerCase();
}

export type CommandKeywordIndex = {
    /** Token to intent, over every locale at once — a grower may text in a language other than their own. */
    readonly intentByToken: ReadonlyMap<string, CommandIntent>;
    /** The keyword a reply tells a grower to send back, per locale. Falls back to the English catalog. */
    readonly canonicalTokenByIntent: Record<Locale, Record<CommandIntent, string>>;
    /** Argument-taking tokens that may match as a prefix, longest first. */
    readonly spacelessTokens: readonly { token: string; intent: CommandIntent }[];
};

/**
 * Takes the catalogs as an argument so a collision can be provoked in a test without corrupting a
 * shipped file. Throws rather than returning: a vocabulary that maps one token to two intents would
 * silently act on the wrong one, so it must fail at import, not at the first grower who hits it.
 */
export function buildCommandKeywordIndex(catalogs: Record<Locale, CommandsCatalog>): CommandKeywordIndex {
    const intentByToken = new Map<string, CommandIntent>();
    const spacelessTokens: { token: string; intent: CommandIntent }[] = [];
    // Only ever read back into a collision message, so the first claimant is all it needs to name.
    const firstLocaleByToken = new Map<string, Locale>();
    const canonicalTokenByIntent = {} as Record<Locale, Record<CommandIntent, string>>;

    LOCALES.forEach((locale) => {
        const catalog = catalogs[locale];
        if (!catalog) {
            throw new Error(`Command catalog missing for locale ${locale}.`);
        }

        canonicalTokenByIntent[locale] = {} as Record<CommandIntent, string>;

        Object.entries(catalog).forEach(([rawIntent, tokens]) => {
            if (!(COMMAND_INTENTS as readonly string[]).includes(rawIntent)) {
                throw new Error(
                    `Unknown command intent "${rawIntent}" in ${locale}. A key the parser does not know would ` +
                        "accept keywords that reach no handler.",
                );
            }
            const intent = rawIntent as CommandIntent;

            tokens.forEach((rawToken) => {
                const token = normalizeCommandToken(rawToken);
                const claimed = intentByToken.get(token);
                if (claimed !== undefined && claimed !== intent) {
                    throw new Error(
                        `Command keyword collision: "${token}" is \`${claimed}\` in ` +
                            `${firstLocaleByToken.get(token)} and \`${intent}\` in ${locale}. A keyword must ` +
                            "mean one thing across every locale — a grower may text in any language.",
                    );
                }
                intentByToken.set(token, intent);
                firstLocaleByToken.set(token, firstLocaleByToken.get(token) ?? locale);
                if (ARGUMENT_TAKING_INTENTS.includes(intent) && SPACELESS_FINAL_CHARACTER.test(token)) {
                    spacelessTokens.push({ token, intent });
                }
            });

            // Resolved once here rather than per reply, so a locale missing an intent falls back at build.
            const [canonical] = tokens;
            if (canonical) canonicalTokenByIntent[locale][intent] = canonical;
        });
    });

    LOCALES.forEach((locale) => {
        COMMAND_INTENTS.forEach((intent) => {
            canonicalTokenByIntent[locale][intent] ??= canonicalTokenByIntent[DEFAULT_LOCALE][intent];
        });
    });

    return {
        intentByToken,
        canonicalTokenByIntent,
        spacelessTokens: spacelessTokens.sort((a, b) => b.token.length - a.token.length),
    };
}

export const commandKeywordIndex: CommandKeywordIndex = buildCommandKeywordIndex(commandCatalogs);
